import { useRef, useState } from 'react';
import { NJButton, NJFormItem, NJRadio, NJRadioGroup, NJToggle } from '@engie-group/fluid-design-system-react';
import { Size2MixRightSideModal } from '@shared/size2mix-shell';
import type { ImportExportFormat, ImportOptions, ImportRequest } from './types';

/** Toggles measured on integration (20-10-2026), in display order with their default state. */
const OPENGIS_OPTIONS: {
    id: keyof ImportOptions;
    label: string;
    default: boolean;
}[] = [
    {
        id: 'altitude_retrieval',
        label: "Récupération de l'altimétrie",
        default: false,
    },
    {
        id: 'generate_trace_network',
        label: 'Récupération du tracé',
        default: true,
    },
    {
        id: 'generate_substations',
        label: 'Récupération des sous-stations',
        default: true,
    },
    {
        id: 'generate_plants',
        label: 'Récupération des départs et des retours',
        default: true,
    },
    {
        id: 'generate_exchange_sub',
        label: "Récupération des sous-stations d'échange",
        default: false,
    },
    { id: 'generate_valves', label: 'Récupération des vannes', default: false },
    {
        id: 'generate_vspeedpump',
        label: 'Récupération des pompes à vitesse variable',
        default: false,
    },
    {
        id: 'replace_bends_to_junctions',
        label: 'Remplacer un coude par une jonction en cas de superposition',
        default: false,
    },
];
/** Format NEMO keeps only the altimetry toggle. */
const NEMO_OPTIONS = OPENGIS_OPTIONS.slice(0, 1);

const defaults = () => Object.fromEntries(OPENGIS_OPTIONS.map((o) => [o.id, o.default])) as unknown as ImportOptions;

export interface ImportDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (request: ImportRequest) => void;
}

export function ImportDrawer({ isOpen, onClose, onSubmit }: ImportDrawerProps) {
    const [format, setFormat] = useState<ImportExportFormat>('OpenGIS');
    const [name, setName] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [options, setOptions] = useState<ImportOptions>(defaults);
    // Production (ImportsModal `isCreated`): after a successful submit the drawer shows SuccessImport.
    const [created, setCreated] = useState(false);
    const fileInput = useRef<HTMLInputElement>(null);

    const reset = () => {
        setFormat('OpenGIS');
        setName('');
        setFile(null);
        setOptions(defaults());
        setCreated(false);
    };
    const close = () => {
        reset();
        onClose();
    };
    const submit = () => {
        // Production (yup `file: required`): without a file the submit is blocked and no message is displayed.
        if (!file) return;
        const visible = format === 'NEMO' ? NEMO_OPTIONS : OPENGIS_OPTIONS;
        onSubmit({
            format,
            name,
            fileName: file.name,
            options: Object.fromEntries(visible.map((o) => [o.id, options[o.id]])),
        });
        setCreated(true);
    };

    return (
        <Size2MixRightSideModal
            isOpen={isOpen}
            onClose={close}
            title={format === 'NEMO' ? 'Importer un nouveau réseau' : 'Importer un réseau'}
            testId="createListImportForm"
            footer={created ? <NJButton label="Fermer" onClick={close} /> : undefined}
        >
            {created ? (
                <div
                    style={{
                        height: '50vh',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 'var(--nj-semantic-size-spacing-24)',
                    }}
                >
                    <span className="material-icons" aria-hidden="true" style={{ fontSize: 64, color: 'var(--nj-semantic-color-icon-status-success-primary-default)' }}>
                        check_circle
                    </span>
                    <p className="nj-text nj-text--xl" style={{ margin: 0, padding: 8, textAlign: 'center', color: 'var(--nj-semantic-color-text-neutral-primary-default)' }}>
                        Votre réseau est en cours d&apos;import / Vous pouvez dès à présent suivre l&apos;état de votre import dans le tableau des imports
                    </p>
                </div>
            ) : (
                <>
                    {/* Layout measured on integration (drawer 357px): radio group 14% / 15% margins, form 10% / 15% + 12px padding. */}
                    <NJRadioGroup
                        legend=""
                        orientation="row"
                        className="ref-import-format"
                        checkedId={format === 'OpenGIS' ? 'import-format-opengis' : 'import-format-nemo'}
                        onChange={(_id, value) => setFormat(value as ImportExportFormat)}
                    >
                        <NJRadio id="import-format-opengis" name="import-format" value="OpenGIS" label="OpenGIS" />
                        <NJRadio id="import-format-nemo" name="import-format" value="NEMO" label="Format NEMO" />
                    </NJRadioGroup>
                    <style>{`.ref-import-format { margin: 28px 15% 0 14%; } .ref-import-format legend:empty { display: none; }`}</style>
                    <form
                        onSubmit={(event) => {
                            event.preventDefault();
                            submit();
                        }}
                        style={{ padding: 12, margin: '0 15% 0 10%' }}
                    >
                        <div style={{ margin: '8px 0 20px' }}>
                            <NJFormItem id="import-name" label="Nom (optionnel)" value={name} onChange={(event) => setName((event.target as HTMLInputElement).value)} />
                        </div>
                        <div style={{ paddingTop: 8 }}>
                            <h4 style={{ margin: 0, padding: '8px 0', fontSize: 16, fontWeight: 700 }}>Télécharger un fichier (.zip)</h4>
                            {/* Production: label in English, subtle grey button (grey-100 / grey-800, no border). */}
                            <button
                                type="button"
                                className="nj-btn nj-btn--subtle"
                                onClick={() => fileInput.current?.click()}
                                style={{ height: 40, border: 'none', background: 'var(--nj-core-color-grey-100)', color: 'var(--nj-core-color-grey-800)' }}
                            >
                                <span className="material-icons nj-btn__icon" aria-hidden="true">
                                    attachment
                                </span>
                                Choose a file
                            </button>
                            {file && (
                                <p className="nj-text nj-text--sm" style={{ margin: '8px 0 0', overflowWrap: 'anywhere' }}>
                                    {file.name}
                                </p>
                            )}
                            <input ref={fileInput} type="file" accept=".zip" hidden onChange={(event) => setFile(event.target.files?.[0] ?? null)} />
                        </div>
                        {/* Production: OpenGIS toggles follow the `mb-2` wrapper; the NEMO toggle sits inside it. */}
                        {format === 'OpenGIS' && <div style={{ height: 8 }} aria-hidden="true" />}
                        {(format === 'NEMO' ? NEMO_OPTIONS : OPENGIS_OPTIONS).map((option) => (
                            <div key={option.id} style={{ padding: '8px 0' }}>
                                <NJToggle
                                    id={option.id}
                                    label={option.label}
                                    checked={options[option.id]}
                                    onChange={(_event, checked) => setOptions((current) => ({ ...current, [option.id]: checked }))}
                                />
                            </div>
                        ))}
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: 20,
                                // Production: NemoFormat pushes the footer down (`mb-2` + `mt-60`), OpenGisFormat uses `my-4`.
                                margin: format === 'NEMO' ? '248px 0 0' : '16px 0',
                                padding: 16,
                                borderTop: '1px solid var(--nj-core-color-grey-300)',
                            }}
                        >
                            <NJButton label="Annuler" variant="secondary" onClick={close} />
                            <NJButton label="Valider" type="submit" />
                        </div>
                    </form>
                </>
            )}
        </Size2MixRightSideModal>
    );
}
